import axios from 'axios';
import store from '../redux/store';
import * as authHandlers from '../routes/Auth/redux/handlers';
import { isTokenValid, shouldRefreshToken } from './auth';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    
    failedQueue = [];
};

// Request interceptor to add token to headers
axios.interceptors.request.use(
    async (config) => {
        const token = localStorage.getItem('accessToken');
        
        // Check if token needs refresh before making request
        if (shouldRefreshToken() && !isRefreshing) {
            isRefreshing = true;
            try {
                await store.dispatch(authHandlers.refreshTokenHandler());
                const newToken = localStorage.getItem('accessToken');
                if (newToken) {
                    config.headers.Authorization = `Bearer ${newToken}`;
                }
            } catch (error) {
                console.error('Token refresh failed:', error);
            } finally {
                isRefreshing = false;
            }
        } else if (token && isTokenValid(token)) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle 401 errors
axios.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axios(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }
            
            originalRequest._retry = true;
            isRefreshing = true;
            
            const refreshToken = localStorage.getItem('refreshToken');
            
            if (refreshToken && isTokenValid(refreshToken)) {
                try {
                    const response = await store.dispatch(authHandlers.refreshTokenHandler());
                    
                    if (response?.data?.access_token) {
                        const newToken = response.data.access_token;
                        localStorage.setItem('accessToken', newToken);
                        processQueue(null, newToken);
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return axios(originalRequest);
                    }
                } catch (refreshError) {
                    processQueue(refreshError, null);
                    // Logout user if refresh fails
                    await store.dispatch(authHandlers.logoutHandler());
                    window.location.href = '/auth/login';
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            } else {
                // No valid refresh token, logout user
                await store.dispatch(authHandlers.logoutHandler());
                window.location.href = '/auth/login';
            }
        }
        
        return Promise.reject(error);
    }
);

export default axios;